<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Badge from 'dashboard/components-next/badge/Badge.vue';

import { getContratoTarifas } from 'dashboard/api/supra';

const props = defineProps({
  numeroContrato: {
    type: String,
    required: true,
  },
  explotacion: {
    type: String,
    required: true,
  },
});

useI18n();

const tarifa = ref(null);
const isLoading = ref(true);
const error = ref(null);

const formatCurrency = value => {
  const num = parseFloat(value || 0);
  return num.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
};

const formatPct = value => {
  if (value == null) return '-';
  return `${(parseFloat(value) * 100).toFixed(0)}%`;
};

const formatDate = dateStr => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const fetchTarifa = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const res = await getContratoTarifas(props.numeroContrato, props.explotacion);
    const data = res.data?.data;
    if (!data) throw new Error('No se encontraron tarifas para este contrato');
    tarifa.value = data;
  } catch (err) {
    error.value = err.message || 'Error al cargar tarifas';
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchTarifa();
});
</script>

<template>
  <div class="space-y-6">
    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <Spinner :size="32" />
        <p class="text-sm text-n-slate-11 mt-2">
          {{ $t('CONTRACTS.TARIFAS.LOADING') }}
        </p>
      </div>
    </div>

    <!-- Error / Empty -->
    <div
      v-else-if="error || !tarifa"
      class="flex flex-col items-center justify-center py-12 gap-4"
    >
      <span class="i-lucide-alert-circle text-6xl text-n-ruby-11" />
      <p class="text-n-slate-11">
        {{ error || $t('CONTRACTS.TARIFAS.NOT_FOUND') }}
      </p>
    </div>

    <!-- SUPRA: Bloques tarifarios -->
    <div v-else-if="tarifa.fuente === 'supra'" class="space-y-4">
      <!-- Header -->
      <div class="bg-n-background border border-n-weak rounded-lg p-4">
        <h3 class="text-lg font-semibold text-n-slate-12 mb-1 flex items-center gap-2">
          <span class="i-lucide-dollar-sign" />
          {{ tarifa.tipoUso || $t('CONTRACTS.TARIFAS.APPLIED_TARIFF') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ $t('CONTRACTS.TARIFAS.CODE_PREFIX', { code: tarifa.tipoUsoCodigo || '-' }) }}
        </p>
      </div>

      <!-- Bloques table -->
      <div class="bg-n-background border border-n-weak rounded-lg overflow-hidden">
        <div class="px-4 py-3 border-b border-n-weak">
          <h4 class="font-semibold text-n-slate-12 flex items-center gap-2">
            <span class="i-lucide-layers" />
            {{ $t('CONTRACTS.TARIFAS.INCLUDED_SERVICES') }}
          </h4>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-n-solid-3">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase">Bloque</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase">Rango (m³)</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase">Precio/m³</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase">Cuota Fija</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase">Alcantarillado</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase">Saneamiento</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase">IVA</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase">Vigencia</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="bloque in tarifa.bloques"
                :key="bloque.orden"
                class="hover:bg-n-alpha-1"
              >
                <td class="px-4 py-3 text-center">
                  <Badge color="slate" size="small">{{ bloque.orden }}</Badge>
                </td>
                <td class="px-4 py-3 text-right text-sm text-n-slate-12 font-mono">
                  {{ bloque.rangoMinM3 }} – {{ bloque.rangoMaxM3 ?? '∞' }}
                </td>
                <td class="px-4 py-3 text-right text-sm font-semibold text-n-slate-12">
                  {{ formatCurrency(bloque.precioM3) }}
                </td>
                <td class="px-4 py-3 text-right text-sm text-n-slate-12">
                  {{ formatCurrency(bloque.cuotaFija) }}
                </td>
                <td class="px-4 py-3 text-right text-sm text-n-slate-11">
                  {{ formatPct(bloque.pctAlcantarillado) }}
                </td>
                <td class="px-4 py-3 text-right text-sm text-n-slate-11">
                  {{ formatPct(bloque.pctSaneamiento) }}
                </td>
                <td class="px-4 py-3 text-right text-sm text-n-slate-11">
                  {{ formatPct(bloque.pctIva) }}
                </td>
                <td class="px-4 py-3 text-sm text-n-slate-11">
                  {{ formatDate(bloque.vigenciaDesde) }}
                  <span v-if="bloque.vigenciaHasta"> – {{ formatDate(bloque.vigenciaHasta) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- CEA: Subconceptos / variables -->
    <div v-else class="space-y-6">
      <!-- Header -->
      <div class="grid gap-4 md:grid-cols-3">
        <div class="bg-n-background border border-n-weak rounded-lg p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs text-n-slate-11 font-medium">
              {{ $t('CONTRACTS.TARIFAS.APPLIED_TARIFF') }}
            </p>
            <span class="i-lucide-dollar-sign text-n-slate-11" />
          </div>
          <div class="text-xl font-bold text-n-slate-12">{{ tarifa.descripcion }}</div>
          <p class="text-xs text-n-slate-10 mt-1">
            {{ $t('CONTRACTS.TARIFAS.CODE_PREFIX', { code: `${tarifa.codigo?.id1}-${tarifa.codigo?.id2}-${tarifa.codigo?.id3}` }) }}
          </p>
        </div>
        <div class="bg-n-background border border-n-weak rounded-lg p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs text-n-slate-11 font-medium">
              {{ $t('CONTRACTS.TARIFAS.PUBLICATION') }}
            </p>
            <span class="i-lucide-calendar text-n-slate-11" />
          </div>
          <div class="text-sm font-medium text-n-slate-12">
            {{ formatDate(tarifa.publicacion?.fechaPublicacion) }}
          </div>
          <p class="text-xs text-n-slate-10 mt-1 line-clamp-2">
            {{ tarifa.publicacion?.textoPublicacion || $t('CONTRACTS.TARIFAS.NO_INFO') }}
          </p>
        </div>
        <div class="bg-n-background border border-n-weak rounded-lg p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs text-n-slate-11 font-medium">
              {{ $t('CONTRACTS.TARIFAS.SERVICES_COUNT') }}
            </p>
            <span class="i-lucide-droplet text-n-slate-11" />
          </div>
          <div class="text-2xl font-bold text-n-slate-12">{{ tarifa.subconceptos?.length ?? 0 }}</div>
          <p class="text-xs text-n-slate-10 mt-1">{{ $t('CONTRACTS.TARIFAS.SERVICE_TYPES') }}</p>
        </div>
      </div>

      <!-- Subconceptos -->
      <div v-if="tarifa.subconceptos?.length" class="bg-n-background border border-n-weak rounded-lg p-4">
        <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
          {{ $t('CONTRACTS.TARIFAS.INCLUDED_SERVICES') }}
        </h3>
        <div class="space-y-4">
          <div
            v-for="(sub, index) in tarifa.subconceptos"
            :key="index"
            class="border border-n-weak rounded-lg p-4 space-y-3"
          >
            <div class="flex items-start justify-between">
              <h4 class="font-semibold text-lg flex items-center gap-2 text-n-slate-12">
                <span class="i-lucide-droplet text-n-brand" />
                {{ sub.descripcion }}
              </h4>
              <Badge color="slate" size="small">
                {{ sub.correctores?.length ?? 0 }} {{ $t('CONTRACTS.TARIFAS.CORRECTORS') }}
              </Badge>
            </div>
            <div v-if="sub.correctores?.length" class="border-t border-n-weak pt-3">
              <p class="text-sm font-medium mb-2 flex items-center gap-2 text-n-slate-12">
                <span class="i-lucide-settings" />
                {{ $t('CONTRACTS.TARIFAS.CORRECTORS_AND_POLICIES') }}
              </p>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div
                  v-for="(corrector, ci) in sub.correctores"
                  :key="ci"
                  class="flex items-start gap-2 text-sm p-2 bg-n-alpha-1 rounded-lg"
                >
                  <span class="i-lucide-info text-n-slate-11 mt-0.5 flex-shrink-0" />
                  <span class="text-n-slate-12">{{ corrector.descripcion }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Variables contrato -->
      <div v-if="tarifa.variablesContratos?.length" class="bg-n-background border border-n-weak rounded-lg p-4">
        <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
          {{ $t('CONTRACTS.TARIFAS.CONTRACT_VARIABLES') }}
        </h3>
        <table class="w-full">
          <thead>
            <tr class="border-b border-n-weak">
              <th class="text-left py-2 px-3 text-xs font-semibold text-n-slate-11">{{ $t('CONTRACTS.TARIFAS.ID_COL') }}</th>
              <th class="text-left py-2 px-3 text-xs font-semibold text-n-slate-11">{{ $t('CONTRACTS.TARIFAS.DESCRIPTION_COL') }}</th>
              <th class="text-right py-2 px-3 text-xs font-semibold text-n-slate-11">{{ $t('CONTRACTS.TARIFAS.VALUE_COL') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(v, i) in tarifa.variablesContratos"
              :key="i"
              class="border-b border-n-weak hover:bg-n-alpha-1"
            >
              <td class="py-2 px-3">
                <Badge color="slate" size="small" class="font-mono">{{ v.id }}</Badge>
              </td>
              <td class="py-2 px-3 text-sm font-medium text-n-slate-12">{{ v.descripcion }}</td>
              <td class="py-2 px-3 text-sm text-right font-mono text-n-slate-12">{{ v.valor }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Variables punto de servicio -->
      <div v-if="tarifa.variablesPuntoServicio?.length" class="bg-n-background border border-n-weak rounded-lg p-4">
        <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
          {{ $t('CONTRACTS.TARIFAS.SERVICE_POINT_VARIABLES') }}
        </h3>
        <table class="w-full">
          <thead>
            <tr class="border-b border-n-weak">
              <th class="text-left py-2 px-3 text-xs font-semibold text-n-slate-11">{{ $t('CONTRACTS.TARIFAS.ID_COL') }}</th>
              <th class="text-left py-2 px-3 text-xs font-semibold text-n-slate-11">{{ $t('CONTRACTS.TARIFAS.DESCRIPTION_COL') }}</th>
              <th class="text-right py-2 px-3 text-xs font-semibold text-n-slate-11">{{ $t('CONTRACTS.TARIFAS.VALUE_COL') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(v, i) in tarifa.variablesPuntoServicio"
              :key="i"
              class="border-b border-n-weak hover:bg-n-alpha-1"
            >
              <td class="py-2 px-3">
                <Badge color="slate" size="small" class="font-mono">{{ v.id }}</Badge>
              </td>
              <td class="py-2 px-3 text-sm font-medium text-n-slate-12">{{ v.descripcion }}</td>
              <td class="py-2 px-3 text-sm text-right font-mono text-n-slate-12">{{ v.valor }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
